import { SongTier } from "./Song";

export type UserRole = "user" | "supporter" | "admin";

export interface UserCreditsData {
  songBasic: number;
  songPremium: number;
  songExclusive: number;
}

export interface UserStatsData {
  variantsCreated: number;
  songBasicUsed: number;
  songPremiumUsed: number;
  songExclusiveUsed: number;
  lastSongAt?: string | null;
}

export interface UserProfileData {
  id: string;
  displayName: string;
  email?: string | null;
  photoURL?: string | null;

  role: UserRole;

  credits: UserCreditsData;
  stats: UserStatsData;

  referrerCode?: string | null;
  referredByUserId?: string | null;

  createdAt: string;
  updatedAt: string;
}

export class UserProfile {
  readonly id: string;
  displayName: UserProfileData["displayName"];
  email?: UserProfileData["email"];
  photoURL?: UserProfileData["photoURL"];

  role: UserProfileData["role"];

  credits: UserProfileData["credits"];
  stats: UserProfileData["stats"];

  referrerCode?: UserProfileData["referrerCode"];
  referredByUserId?: UserProfileData["referredByUserId"];

  createdAt: UserProfileData["createdAt"];
  updatedAt: UserProfileData["updatedAt"];

  constructor(data: UserProfileData) {
    this.id = data.id;
    this.displayName = data.displayName ?? null;
    this.email = data.email ?? null;
    this.photoURL = data.photoURL ?? null;
    this.role = data.role;
    this.credits = data.credits;
    this.stats = data.stats;
    this.referrerCode = data.referrerCode ?? null;
    this.referredByUserId = data.referredByUserId ?? null;
    this.createdAt = data.createdAt;
    this.updatedAt = data.updatedAt;
  }

  static init(aId = "", aName = "", aEmail: null | string = null): UserProfile {
    const now = new Date().toISOString();
    const randomAvatarIndex = Math.floor(Math.random() * 4) + 1;
    const data: UserProfileData = {
      id: aId,
      createdAt: now,
      updatedAt: now,
      displayName: aName,
      email: aEmail,
      photoURL: 'noavatar_0' + randomAvatarIndex + '.png',
      role: "user",
      credits: { songBasic: 0, songPremium: 0, songExclusive: 0 },
      stats: { variantsCreated: 0, songBasicUsed: 0, songPremiumUsed: 0, songExclusiveUsed: 0, lastSongAt: null }
    };
    return new UserProfile(data);
  }

  static initFromObject(o: UserProfileData): UserProfile {
    if (!o || typeof o !== "object") {
      throw new Error("Invalid UserData: empty or not an object");
    }
    if (!o.id || !o.createdAt || !o.updatedAt || !o.role || !o.credits || !o.stats) {
      throw new Error("Invalid UserData: missing required fields");
    }
    return new UserProfile(o);
  }

  getDataObject(): UserProfileData {
    return {
      id: this.id,
      displayName: this.displayName ?? null,
      email: this.email ?? null,
      photoURL: this.photoURL ?? null,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      role: this.role,
      credits: this.credits,
      stats: this.stats,
      referrerCode: this.referrerCode ?? null,
      referredByUserId: this.referredByUserId ?? null
    };
  }

  setDisplayName(aName: string) {
    this.displayName = aName;
  }
  setPhotoURL(aUrl: string) {
    this.photoURL = aUrl;
  }

  touchUpdated() {
    this.updatedAt = new Date().toISOString();
  }

  convertAnonUser(aName: string, aEmail: string) {
    this.displayName = aName;
    this.email = aEmail;
  }

  isGuest(): boolean {
    return this.email?.length == 0;
  }

  getAvailableTiers(): SongTier[] {
  const tiers: SongTier[] = [];
  if (this.credits.songBasic > 0) tiers.push("basic");
  if (this.credits.songPremium > 0) tiers.push("premium");
  if (this.credits.songExclusive > 0) tiers.push("exclusive");
  return tiers;
}
}

export interface PublicUserData {
  displayName: string;
  photoURL?: string | null;
  role: UserRole;
  stats: UserStatsData;
}

export class PublicUserProfile {
  displayName: string;
  photoURL?: string | null;
  role: UserRole;
  stats: UserStatsData;

  constructor(data: PublicUserData) {
    this.displayName = data.displayName;
    this.photoURL = data.photoURL;
    this.role = data.role;
    this.stats = data.stats;
  }

  getDataObject(): PublicUserData {
    return {
      displayName: this.displayName,
      photoURL: this.photoURL,
      role: this.role,
      stats: this.stats,
    };
  }

  static initFromObject(o: PublicUserData): PublicUserProfile {
    if (!o || typeof o !== "object") {
      throw new Error("Invalid PublicUserData: empty or not an object");
    }
    return new PublicUserProfile(o);
  }
}
